import React, { useContext, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { secured } from "react-abac";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, EventSurveyQuestionTypesEnum, EventSurveyStatusEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Badge, Button, Card, Dropdown, Grid, Icon, Spinner, Text, Section, Alert, Modal } from "~/components-shared";
import CoreContext from "~/lib/context";
import { useDispatch, useSelector } from "react-redux";
import eventSurveySelectors from "~/store/eventSurveys/selectors";
import eventSurveysActionCreators from "~/store/eventSurveys/actions";
import EventSurveyUpdateModal from "~/components/EventSurveyUpdateModal";
import EventSurveyDeleteModal from "~/components/EventSurveyDeleteModal";
import DefaultSideBar from "~/components/DefaultSideBar";
import { Chart } from "react-chartjs-2";
import lists from "~/lib/lists";
import authSelectors from "~/store/auth/selectors";
import moment from "moment";
import EventSurveyEvaluationQuestion from "~/components/EventSurveyEvaluationQuestion";
import EventSurveyBooleanQuestion from "~/components/EventSurveyBooleanQuestion";
import EventSurveyAttendedQuestion from "~/components/EventSurveyAttendedQuestion";
import eventSurveyCharts from "~/charts/eventSurveys";
import EventSurveyNumberQuestion from "~/components/EventSurveyNumberQuestion";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import ErrorSection from "~/components/ErrorSection";
import useAbacExt from "~/hooks/abac";
import EventSurveyStringQuestion from "~/components/EventSurveyStringQuestion";
const StyledDropdown = styled(Dropdown)`
  display: inline-block;
  vertical-align: bottom;
  margin-left: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 3;
}}px;
`;
const Actions = styled.div`
  float: right;
  display: flex;
  align-items: center;
`;
const StyledGrid = styled(Grid)`
  border: 1px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.borders.primary;
}};
  border-radius: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.shape.borderRadius.primary;
}};

  & > *:not(:last-child) {
    border-right: 1px solid ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.borders.primary;
}};
  }
`;
const EventSurveySurvey = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    t
  } = useTranslation();
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    eventSurveyId
  } = useParams();
  const status = useSelector(eventSurveySelectors.getStatusByEventSurvey(eventSurveyId));
  const action = useSelector(eventSurveySelectors.getAction);
  const error = useSelector(eventSurveySelectors.getErrorByEventSurvey(eventSurveyId));
  const sendStatus = useSelector(eventSurveySelectors.getSendStatus(eventSurveyId));
  const survey = useSelector(eventSurveySelectors.getEventSurvey(eventSurveyId));
  const user = useSelector(authSelectors.getUser);
  const {
    userHasOneOf
  } = useAbacExt();
  const history = useHistory();
  useEffect(() => {
    setSidebar(/*#__PURE__*/React.createElement(DefaultSideBar, null));
  }, [setSidebar]);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(eventSurveysActionCreators.fetchOneStarted(eventSurveyId));
    }
  }, [dispatch, eventSurveyId, status]);
  if (!survey && status === StatusStateEnum.FAILED) {
    return /*#__PURE__*/React.createElement(ErrorSection, {
      error: error
    });
  }
  if (!survey || !user) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const {
    event
  } = survey;
  const memberAttended = !!survey.attended.find(a => a.id === user.id);
  const enableMemberAnswer = !!event.attendees.find(attendee => attendee.id === user.id) && survey.status === EventSurveyStatusEnum.SENT;
  const summary = [{
    title: t("EventSurvey.invited"),
    value: survey.event.attendees.length || 0
  }, {
    title: t("EventSurvey.attended"),
    value: survey.attended.length
  }, {
    title: t("EventSurvey.rate"),
    value: `${survey.attended.length ? Math.round(survey.attended.length / survey.event.attendees.length * 100).toFixed() : 0}%`
  }];
  const handleUpdate = () => {
    dispatch(eventSurveysActionCreators.actionChanged(CRUDActionEnum.UPDATE, survey.id));
  };
  const handleDelete = () => {
    dispatch(eventSurveysActionCreators.actionChanged(CRUDActionEnum.DELETE, survey.id));
  };
  const handleViewEvent = () => {
    history.push(RoutesEnum.EVENT.replace(":eventId", event.id));
  };
  const handleSend = () => {
    dispatch(eventSurveysActionCreators.sendStarted(survey.id));
  };
  return /*#__PURE__*/React.createElement(Section, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "grey",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block",
    onClick: handleViewEvent
  }, t("EventSurvey.viewEvent")), userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey.createdBy.id) && survey.status !== EventSurveyStatusEnum.SENT && /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey?.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleUpdate();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey?.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 1
      },
      color: "danger"
    }), t("common.actions.delete")));
  })), /*#__PURE__*/React.createElement(Text, {
    element: "h1"
  }, t("EventSurvey.survey")), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "bold"
  }, t("EventSurvey.for")), /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`,
    s: {
      mb: 3
    }
  }, event.name), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    fontSize: 4
  }, survey.status === EventSurveyStatusEnum.SENT ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 1
    },
    scale: 3
  }), t("EventSurvey.sentVia"), " ", /*#__PURE__*/React.createElement("strong", null, lists.eventSurveys.target.names[survey.target]), " ", t("EventSurvey.at"), " ", moment(survey.sentAt).format("ddd, MMM D YYYY hh:mma")) : survey.scheduledAt ? /*#__PURE__*/React.createElement(React.Fragment, null, t("EventSurvey.scheduledVia"), " ", /*#__PURE__*/React.createElement("strong", null, lists.eventSurveys.target.names[survey.target]), " ", t("EventSurvey.at"), " ", moment(survey.scheduledAt).format("ddd, MMM D YYYY hh:mma")) : /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.danger.main
  }, t("EventSurvey.notScheduled"), " (", /*#__PURE__*/React.createElement("strong", null, lists.eventSurveys.target.names[survey.target]), ")")), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, survey.status !== EventSurveyStatusEnum.SENT && userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey.createdBy.id) && /*#__PURE__*/React.createElement(Button, {
    color: "primaryGrey",
    s: {
      px: 6,
      py: 2
    },
    rounded: true,
    display: "inline-block",
    loading: sendStatus === StatusStateEnum.LOADING,
    onClick: handleSend
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "share",
    s: {
      mr: 1
    }
  }), t("EventSurvey.sendNow")))), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 3,
    s: {
      mb: 6,
      mt: 6
    },
    equal: true
  }, summary.map(item => /*#__PURE__*/React.createElement(Grid.Cell, {
    key: item.title,
    s: {
      px: 4,
      py: 2
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, item.title), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 8,
    color: theme.colors.misc.darkPurple,
    weight: "bold"
  }, item.value)))), !!survey.questions.length && /*#__PURE__*/React.createElement("div", {
    style: {
      height: 250,
      width: "100%",
      marginBottom: "1em"
    }
  }, /*#__PURE__*/React.createElement(Chart, eventSurveyCharts.answers(theme, survey.questions, true))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(EventSurveyAttendedQuestion, {
    surveyId: survey.id,
    attendedCount: survey.attended.length,
    active: enableMemberAnswer,
    memberAttended: memberAttended
  }), (userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey.createdBy.id) || memberAttended) && survey.questions.map((question, index) => /*#__PURE__*/React.createElement(Card, {
    align: "left",
    key: question.id,
    border: true,
    s: {
      mb: 5
    },
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      fontSize: 5,
      weight: "bold"
    }, index + 1, ". ", question.text), /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      fontSize: 3,
      weight: "semiBold"
    }, t("EventSurvey.answers"), ": ", question.answers.length), /*#__PURE__*/React.createElement(Badge, {
      color: theme.colors.secondary.main,
      s: {
        ml: 3,
        py: 0
      },
      fontSize: 3
    }, lists.eventSurveys.questionTypes.names[question.type])))
  }, question.type === EventSurveyQuestionTypesEnum.EVALUATION && /*#__PURE__*/React.createElement(EventSurveyEvaluationQuestion, {
    surveyId: survey.id,
    questionId: question.id,
    active: enableMemberAnswer && memberAttended && !question.answers.some(a => a.member.id === user.id),
    answer: question.answers.find(a => a.member.id === user.id)?.value
  }), question.type === EventSurveyQuestionTypesEnum.BOOLEAN && /*#__PURE__*/React.createElement(EventSurveyBooleanQuestion, {
    surveyId: survey.id,
    questionId: question.id,
    active: enableMemberAnswer && memberAttended && !question.answers.some(a => a.member.id === user.id),
    answer: question.answers.find(a => a.member.id === user.id)?.value
  }), question.type === EventSurveyQuestionTypesEnum.STRING && /*#__PURE__*/React.createElement(EventSurveyStringQuestion, {
    surveyId: survey.id,
    questionId: question.id,
    active: enableMemberAnswer && memberAttended && !question.answers.some(a => a.member.id === user.id),
    answer: question.answers.find(a => a.member.id === user.id)?.value
  }), question.type === EventSurveyQuestionTypesEnum.INTEGER && /*#__PURE__*/React.createElement(EventSurveyNumberQuestion, {
    surveyId: survey.id,
    questionId: question.id,
    active: enableMemberAnswer && memberAttended && !question.answers.some(a => a.member.id === user.id),
    answer: question.answers.find(a => a.member.id === user.id)?.value
  })))), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(EventSurveyUpdateModal, null)), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(EventSurveyDeleteModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.EVENT_SURVEYS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(EventSurveySurvey), () => true);